import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle, Users, Building2, Clock, CheckCircle2, UserPlus, FileText, ArrowUpRight } from 'lucide-react';
import { dashboardApi } from '../../services/api';
import { DashboardStats, DepartmentComparison } from '../../types/hr';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const Dashboard: React.FC = () => {
  const { user } = useAuth();

  // Fetch dashboard statistics
  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardApi.getStats(),
    refetchInterval: 30000,
  });

  // Fetch department comparison data
  const { data: deptComparison, isLoading: deptLoading, error: deptError } = useQuery<DepartmentComparison>({
    queryKey: ['dashboard-department-comparison'],
    queryFn: () => dashboardApi.getDepartmentComparison(),
    refetchInterval: 30000,
  });

  const isLoading = statsLoading || deptLoading;
  const hasError = statsError || deptError;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50 dark:bg-slate-950">
        <Loader2 className="h-12 w-12 animate-spin text-blue-500 mb-4" />
        <p className="text-slate-500 font-medium animate-pulse uppercase tracking-widest text-[10px]">Synchronizing Analytics...</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-slate-950 p-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-3xl p-10 max-w-lg text-center shadow-xl">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
          <h3 className="text-2xl font-bold text-red-800 dark:text-red-400 mb-4 tracking-tight">Analytics Connection Error</h3>
          <p className="text-red-600 dark:text-red-300 leading-relaxed">
            {(statsError as Error)?.message || (deptError as Error)?.message || 'We encountered a problem retrieving the HR metrics.'}
          </p>
        </div>
      </div>
    );
  }

  const deptData = deptComparison?.data || [];
  const maxValue = 450;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div className="p-8 space-y-8 bg-gray-50 dark:bg-slate-950 min-h-screen font-sans overflow-y-auto">
      {/* --- Page Header --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#143A62] dark:text-white tracking-tight">
            {getGreeting()}, <span className="text-blue-600 font-bold">{user?.first_name || 'Administrator'}</span>
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium text-sm">
            System pulse: <span className="text-emerald-500 font-bold uppercase tracking-widest text-[10px]">Optimal</span> • Everything looks healthy today.
          </p>
        </div>
        
      </div>

      {/* --- Key Metrics Row --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {[
          { label: 'Total Employees', value: stats?.total_employees, icon: Users, color: 'blue' },
          { label: 'Departments', value: stats?.total_departments, icon: Building2, color: 'indigo' },
          { label: 'Pending Approvals', value: stats?.pending_approvals, icon: Clock, color: 'orange', warning: true },
          { label: 'Shortfall', value: stats?.shortfall_employees, icon: AlertCircle, color: 'red' },
          { label: 'New Joinees', value: stats?.to_be_joined, icon: UserPlus, color: 'emerald' },
        ].map((metric, i) => (
          <Card key={i} className="border-none shadow-lg bg-white/70 backdrop-blur-md dark:bg-slate-900/70 hover:shadow-xl transition-all group overflow-hidden">
            <CardContent className="p-6 relative">
              <div className={`absolute -right-4 -bottom-4 opacity-5 transform group-hover:scale-110 transition-transform duration-500`}>
                <metric.icon size={100} />
              </div>
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2 rounded-xl bg-${metric.color}-50 dark:bg-${metric.color}-900/20 text-${metric.color}-600 dark:text-${metric.color}-400`}>
                  <metric.icon size={18} />
                </div>
                <ArrowUpRight className="h-4 w-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
              </div>
              <div className="space-y-1">
                <p className="text-xs font-bold text-slate-400 dark:text-slate-500">{metric.label}</p>
                <p className={`text-2xl font-bold ${metric.warning ? 'text-orange-500' : 'text-[#143A62] dark:text-white'}`}>
                  {String(metric.value || '0').padStart(2, '0')}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* --- Main Analytics Section --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Department Distribution Chart */}
        <Card className="lg:col-span-2 border-none shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
          <CardHeader className="border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-800/30 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle className="text-lg font-bold text-[#143A62] dark:text-white">Department Distribution</CardTitle>
              </div>
              <div className="flex gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#143A62] rounded-full"></div>
                  <span className="text-xs font-bold text-slate-400">2024</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-teal-400 rounded-full"></div>
                  <span className="text-xs font-bold text-slate-400">Current</span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <div className="relative h-[300px] w-full pt-10">
              {/* Chart Grid Lines */}
              <div className="absolute inset-0 pt-10 flex flex-col justify-between pointer-events-none">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="w-full border-t border-slate-100 dark:border-slate-800/50"></div>
                ))}
              </div>

              {/* Bars */}
              <div className="relative h-full flex items-end justify-around px-4">
                {deptData.map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-3 w-full group">
                    <div className="flex gap-1.5 items-end h-[240px]">
                      {/* 2024 Bar */}
                      <div 
                        className="w-3 bg-[#143A62] rounded-t-lg transition-all duration-700 ease-out group-hover:brightness-110 relative" 
                        style={{ height: `${(item.y2024 / maxValue) * 100}%` }}
                      >
                         <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-[9px] font-bold py-0.5 px-1.5 rounded-full z-10">
                           {item.y2024}
                         </div>
                      </div>
                      {/* Current Bar */}
                      <div 
                        className="w-3 bg-teal-400 rounded-t-lg transition-all duration-700 ease-out group-hover:brightness-110 relative delay-100" 
                        style={{ height: `${(item.current / maxValue) * 100}%` }}
                      >
                         <div className="absolute -top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-teal-600 text-white text-[9px] font-bold py-0.5 px-1.5 rounded-full z-10">
                           {item.current}
                         </div>
                      </div>
                    </div>
                    <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 rotate-[-25deg] origin-top-left whitespace-nowrap">
                      {item.dept}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Circular Progress Widgets */}
        <div className="space-y-8">
          {/* Performance Card */}
          <Card className="border-none shadow-xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-md overflow-hidden">
             <CardContent className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-[#143A62] dark:text-white">Performance Review</h3>
                    <p className="text-xs font-bold text-slate-400">Compliance Rate</p>
                  </div>
                  <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  </div>
                </div>
                <div className="flex justify-center relative py-4">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="54" fill="none" stroke="currentColor" strokeWidth="12" className="text-slate-100 dark:text-slate-800" />
                    <circle 
                      cx="64" cy="64" r="54" fill="none" stroke="url(#blueGradient)" strokeWidth="12" 
                      strokeDasharray={2 * Math.PI * 54} 
                      strokeDashoffset={2 * Math.PI * 54 * (1 - (stats?.performance_review_percentage || 0) / 100)} 
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="blueGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#2dd4bf" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-[#143A62] dark:text-white">{stats?.performance_review_percentage || 0}%</span>
                    <span className="text-[10px] font-bold text-slate-400">Target Met</span>
                  </div>
                </div>
             </CardContent>
          </Card>

          {/* Overhead Ratio Card */}
          <Card className="border-none shadow-xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-md overflow-hidden">
             <CardContent className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="space-y-1">
                    <h3 className="text-sm font-bold text-[#143A62] dark:text-white">Overhead Ratio</h3>
                    <p className="text-xs font-bold text-slate-400">Resource Allocation</p>
                  </div>
                  <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
                    <FileText className="h-4 w-4 text-orange-500" />
                  </div>
                </div>
                <div className="flex justify-center relative py-4">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="54" fill="none" stroke="currentColor" strokeWidth="12" className="text-slate-100 dark:text-slate-800" />
                    <circle 
                      cx="64" cy="64" r="54" fill="none" stroke="url(#orangeGradient)" strokeWidth="12" 
                      strokeDasharray={2 * Math.PI * 54} 
                      strokeDashoffset={2 * Math.PI * 54 * (1 - (stats?.overhead_ratio || 0) / 100)} 
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f97316" />
                        <stop offset="100%" stopColor="#fbbf24" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-[#143A62] dark:text-white">{stats?.overhead_ratio || 0}%</span>
                    <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Efficiency</span>
                  </div>
                </div>
             </CardContent>
          </Card>
        </div>
      </div>

      {/* --- Secondary Row: Action Items --- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Upcoming Exit Interviews', value: stats?.upcoming_exit_interviews, color: 'orange' },
          { label: 'Pending Letter Requests', value: stats?.pending_letter_requests, color: 'amber' },
          { label: 'Employees in Notice', value: stats?.in_notice_period, color: 'blue' },
          { label: 'Active Recruitment', value: stats?.diversity?.female_percentage, color: 'emerald', labelOverride: 'Success Rate' },
        ].map((item, i) => (
          <div key={i} className="p-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-lg flex items-center justify-between group hover:border-blue-200 dark:hover:border-blue-900 transition-all">
            <div className="space-y-1">
              <p className="text-xs font-bold text-slate-400 dark:text-slate-500">{item.labelOverride || item.label}</p>
              <p className="text-xl font-bold text-[#143A62] dark:text-white">{String(item.value || '0').padStart(2, '0')}</p>
            </div>
            <div className={`h-2 w-12 rounded-full bg-${item.color}-500/20 overflow-hidden`}>
               <div className={`h-full bg-${item.color}-500 transition-all duration-1000`} style={{ width: '65%' }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;